import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { merchantApi } from "@/lib/api/merchant";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { LogoMark, Wordmark } from "@/components/brand";
import { bpTrack } from "@/lib/track";

const inputCls =
  "w-full rounded-xl border bg-transparent px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40";

/**
 * BitPays merchant login — username or email, password, Google Authenticator.
 *
 * The identifier field takes either. A username names exactly ONE account,
 * which matters because an email is unique per country now: an owner trading
 * in two markets holds a merchant ID in each, under the same address. Signing
 * in by email still works and lands on the account whose password matches,
 * with a switcher in the header for the rest.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<"password" | "2fa" | "change" | "apikeys">("password");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loginToken, setLoginToken] = useState("");
  const [changeToken, setChangeToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const pwPolicy = (pw: string): string | null => {
    if (pw.length < 12) return "At least 12 characters";
    if (!/[a-z]/.test(pw)) return "Add a lowercase letter";
    if (!/[A-Z]/.test(pw)) return "Add an uppercase letter";
    if (!/[0-9]/.test(pw)) return "Add a number";
    if (!/[^A-Za-z0-9]/.test(pw)) return "Add a special character";
    return null;
  };

  const finish = (token: string) => {
    setAuth(token);
    navigate("/overview");
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res: any = await merchantApi.dashboardLogin(email.trim(), password);
      if (res.must_change_password && res.change_token) {
        setChangeToken(res.change_token);
        setNewPw(""); setConfirmPw("");
        setMode("change");
        toast.message("Please set a new password to continue.");
      } else if (res.requires_2fa && res.login_token) {
        setLoginToken(res.login_token);
        setMode("2fa");
      } else if (res.access_token) {
        if (res.totp_setup_required) toast.message("Tip: enable Google Authenticator in Settings → Security.");
        finish(res.access_token);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submit2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await merchantApi.verify2fa(loginToken, code.trim());
      bpTrack("login_completed");
      finish(res.access_token);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  const submitChange = async (e: React.FormEvent) => {
    e.preventDefault();
    const policy = pwPolicy(newPw);
    if (policy) return toast.error(policy);
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      const res = await merchantApi.changePasswordFirst(changeToken, newPw);
      toast.success("Password updated");
      finish(res.access_token);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Could not set password");
    } finally {
      setSubmitting(false);
    }
  };

  const submitApiKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await merchantApi.login(clientId.trim(), clientSecret.trim());
      bpTrack("login_completed");
      finish(res.access_token);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-sidebar p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bp-gradient opacity-30 blur-3xl" />
        <div className="flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <Wordmark on="dark" className="text-2xl" />
        </div>
        <div className="relative">
          <h2 className="max-w-md text-2xl font-bold leading-tight">
            Accept payments. Get paid faster.
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/60">
            Your BitPays merchant console — orders, settlements and payouts in one place. bitpays.live
          </p>
        </div>
        <span className="text-xs text-white/40">© BitPays</span>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <LogoMark className="h-9 w-9" />
            <Wordmark />
          </div>

          {mode === "password" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
              <p className="mb-6 mt-1 text-sm text-muted-foreground">Use your username (or email) and password</p>
              <form onSubmit={submitPassword} className="flex flex-col gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Username or email</label>
                  <input
                    className={inputCls}
                    // Not type="email": the browser would refuse to submit a
                    // username, which is now a valid identifier here.
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="acme-in"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Password</label>
                  <input className={inputCls} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" disabled={submitting} className="mt-2">
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
              </form>
              <button type="button" onClick={() => setMode("apikeys")} className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline">
                Sign in with API keys instead
              </button>
            </>
          )}

          {mode === "2fa" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight">Two-factor authentication</h1>
              <p className="mb-6 mt-1 text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app — or a recovery code.</p>
              <form onSubmit={submit2fa} className="flex flex-col gap-4">
                <input
                  className={`${inputCls} text-center text-lg tracking-[0.3em]`}
                  autoFocus
                  maxLength={11}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9a-fA-F-]/g, ""))}
                  required
                />
                <Button type="submit" disabled={submitting || code.trim().length < 6}>
                  {submitting ? "Verifying…" : "Verify"}
                </Button>
              </form>
              <button type="button" onClick={() => { setMode("password"); setCode(""); }} className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline">
                ← Back
              </button>
            </>
          )}

          {mode === "change" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight">Set a new password</h1>
              <p className="mb-6 mt-1 text-sm text-muted-foreground">Your account requires a new password before you continue.</p>
              <form onSubmit={submitChange} className="flex flex-col gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">New password</label>
                  <input className={inputCls} type="password" autoFocus autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
                  <p className={`mt-1 text-xs ${newPw && pwPolicy(newPw) ? "text-amber-500" : "text-muted-foreground"}`}>
                    {newPw ? (pwPolicy(newPw) ?? "Strong password ✓") : "Min 12 chars, upper + lower + number + special."}
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Confirm password</label>
                  <input className={inputCls} type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required />
                  {confirmPw && confirmPw !== newPw && <p className="mt-1 text-xs text-red-500">Passwords do not match.</p>}
                </div>
                <Button type="submit" disabled={submitting || !!pwPolicy(newPw) || newPw !== confirmPw}>
                  {submitting ? "Saving…" : "Set password & continue"}
                </Button>
              </form>
            </>
          )}

          {mode === "apikeys" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
              <p className="mb-6 mt-1 text-sm text-muted-foreground">Use your API credentials</p>
              <form onSubmit={submitApiKeys} className="flex flex-col gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Client ID</label>
                  <input className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)} required />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Client Secret</label>
                  <input className={inputCls} type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} required />
                </div>
                <Button type="submit" disabled={submitting} className="mt-2">
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
              </form>
              <button type="button" onClick={() => setMode("password")} className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline">
                ← Sign in with username and password
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
